import { createRootRoute, Outlet, useRouter } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import {
  Toaster,
  useAuth,
  AuthBootstrapper,
  SidebarProvider,
  SidebarInset,
  TopNavbar,
  LocaleProvider,
  Button,
} from '@org/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTrpcClient, trpc } from '@org/clients/school';
import { createGraphTrpcClient, graphTrpc } from '@org/clients/graph';
import { createCommunityTrpcClient, communityTrpc } from '@org/clients/community';
import React, { useMemo, useRef, useState } from 'react';
import superjson from 'superjson';
import { withAuthenticationRequired, useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { NotificationDropdown } from '@/components/notification-dropdown';
import { MessagesBadge } from '@/components/messages-badge';
import { SearchBar } from '@/components/search-bar';
import { AppSidebar } from '@/components/app-sidebar';
import { CommunityBottomTabs } from '@/components/bottom-tabs';
import { RealtimeWatchers } from '@/components/realtime-watchers';

type AuthUser = ReturnType<typeof useAuth0>['user'];

// Create a proper React component for tRPC providers
function TrpcProvider({ children }: { children: React.ReactNode }) {
  const { getAccessToken } = useAuth();
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
    },
  }));

  const trpcClient = useMemo(() => {
    return createTrpcClient({
      queryClient,
      getAccessToken,
      apiUrl: import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:3333/api',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  const graphClient = useMemo(() => {
    return createGraphTrpcClient({
      queryClient,
      getAccessToken,
      apiUrl: import.meta.env.VITE_GRAPH_BACKEND_URL ?? 'http://localhost:3333/graph',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  const communityClient = useMemo(() => {
    return createCommunityTrpcClient({
      queryClient,
      getAccessToken,
      apiUrl: import.meta.env.VITE_COMMUNITY_BACKEND_URL ?? 'http://localhost:3333/community',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <graphTrpc.Provider client={graphClient} queryClient={queryClient}>
        <communityTrpc.Provider client={communityClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <AuthBootstrapper />
            {children}
          </QueryClientProvider>
        </communityTrpc.Provider>
      </graphTrpc.Provider>
    </trpc.Provider>
  );
}

function Header({ user }: { user: AuthUser }) {
  const { t } = useTranslation('community');

  const displayName =
    user?.given_name ?? user?.nickname ?? user?.email ?? t('layout.fallbacks.userName');

  return (
    <TopNavbar
      eyebrowText={t('layout.headerEyebrow', { defaultValue: 'Community' })}
      displayName={displayName}
      user={user}
      app="community"
      logoutLabel={t('userMenu.signOut')}
      loggingOutLabel={t('userMenu.signingOut')}
      editProfileHref="/profile"
      editProfileLabel={t('profile.editProfile')}
      centerElements={<SearchBar />}
      extraElements={
        <>
          <MessagesBadge />
          <NotificationDropdown />
        </>
      }
    />
  );
}

function RootLayoutInner() {
  const { user } = useAuth0();
  return (
    <TrpcProvider>
      <LocaleProvider>
        <SidebarProvider defaultOpen={false} className="bg-muted/50">
          <AppSidebar />
          <RealtimeWatchers />
          <SidebarInset className="bg-muted/50" aria-label="Main content">
            <div className="flex min-h-svh flex-col">
              <Header user={user} />
              <main className="flex-1 overflow-y-auto p-4 pb-20 lg:p-6 lg:pb-6">
                <div className="mx-auto w-full max-w-6xl">
                  <Outlet />
                </div>
              </main>
              <CommunityBottomTabs />
            </div>
          </SidebarInset>
        </SidebarProvider>
        <TanStackRouterDevtools />
        <Toaster position="top-right" visibleToasts={3} expand={false} closeButton />
      </LocaleProvider>
    </TrpcProvider>
  );
}

function RouteErrorComponent({ error }: { error: Error }) {
  const { t } = useTranslation('community');
  const router = useRouter();

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-muted/50 p-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-destructive"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-foreground">
        {t('common.somethingWentWrong')}
      </h2>
      <p className="max-w-md text-muted-foreground">
        {t('common.errorDescription')}
      </p>
      <Button
        variant="red"
        onClick={() => router.invalidate()}
        className="mt-2 rounded-lg px-6 text-white hover:bg-brand/90"
      >
        {t('common.retry')}
      </Button>
    </div>
  );
}

function RootLayout() {
  const isBypassEnabled = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';
  const { t } = useTranslation('community');
  const redirectMessageRef = useRef(t('layout.status.redirecting'));
  redirectMessageRef.current = t('layout.status.redirecting');

  const GuardedRoot = useMemo(() => {
    if (isBypassEnabled) {
      return RootLayoutInner;
    }

    const RedirectingMessage = () => (
      <div className="flex min-h-screen items-center justify-center">
        <span>{redirectMessageRef.current}</span>
      </div>
    );

    return withAuthenticationRequired(RootLayoutInner, {
      onRedirecting: () => <RedirectingMessage />,
    });
  }, [isBypassEnabled]);

  return <GuardedRoot />;
}

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RouteErrorComponent,
});
